import type { Call } from "@frontierx/types";
import { format, isToday, isTomorrow } from "date-fns";
import { Calendar, Clock, FileText, List, Phone, Video } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useFrontendCallTransitions } from "../../hooks/useFrontendCallTransitions";
import { useOpenUrl } from "../../hooks/useOpenUrl";
import { getDisplayIdFromCall } from "../../utils/callDisplayUtils";
import {
  canJoinCall,
  canRejoinCall,
  getDesktopStatusText,
} from "../../utils/callFilters";
import { CallServiceIcon } from "./CallServiceIcon";

export interface NowNextRecentCallsProps {
  calls: Call[];
  onJoinCall: (callId: string) => void;
  onViewReport: (callId: string) => void;
}

export function NowNextRecentCalls({
  calls,
  onJoinCall,
  onViewReport,
}: Readonly<NowNextRecentCallsProps>) {
  const openUrl = useOpenUrl();
  const { now, upNext, recent } = useFrontendCallTransitions(calls);

  const handleJoinExternal = async (call: Call) => {
    if (call.meetingUrl) {
      await openUrl(call.meetingUrl);
    }
  };

  const handleViewAllHistory = async () => {
    const dashUrl = import.meta.env.VITE_DASH_URL;
    if (dashUrl) {
      await openUrl(`${dashUrl}/calls`);
    }
  };

  const formatCallTime = (startTime: string) => {
    const date = new Date(startTime);
    if (isToday(date)) {
      return format(date, "h:mm a");
    }
    if (isTomorrow(date)) {
      return `Tomorrow ${format(date, "h:mm a")}`;
    }
    return format(date, "MMM d, h:mm a");
  };

  return (
    <div className="space-y-1 py-2 px-2">
      {/* NOW Section */}
      <div className="space-y-1 pb-1 border-b border-gray-100">
        <h3 className="flex items-center gap-2 text-green-700 font-semibold text-sm py-0.5 px-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          Now
        </h3>
        <div className="space-y-1">
          {now.length > 0 ? (
            now.map((call) => (
              <CallRow
                key={call.id}
                call={call}
                timeText="Live"
                showJoinButton={canJoinCall(call)}
                onJoin={() => handleJoinExternal(call)}
                onJoinFrontier={() => onJoinCall(call.id)}
                onViewReport={() => onViewReport(call.id)}
                isCompact={true}
              />
            ))
          ) : (
            <div className="text-center py-2 text-xs text-gray-400">
              No active calls
            </div>
          )}
        </div>
      </div>

      {/* UP NEXT Section */}
      <div className="space-y-1 pb-1 border-b border-gray-100">
        <h3 className="flex items-center gap-2 text-blue-700 font-semibold text-sm py-0.5 px-2">
          <Clock className="h-3 w-3" />
          Up next
        </h3>
        <div className="space-y-1">
          {upNext.length > 0 ? (
            upNext.map((call) => (
              <CallRow
                key={call.id}
                call={call}
                timeText={formatCallTime(call.startTime)}
                showJoinButton={canJoinCall(call)}
                onJoin={() => handleJoinExternal(call)}
                onJoinFrontier={() => onJoinCall(call.id)}
                onViewReport={() => onViewReport(call.id)}
                isCompact={true}
              />
            ))
          ) : (
            <div className="text-center py-2 text-xs text-gray-400">
              No upcoming calls
            </div>
          )}
        </div>
      </div>

      {/* RECENT Section */}
      <div className="space-y-1">
        <div className="flex items-center justify-between py-0.5 px-2">
          <h3 className="flex items-center gap-2 text-gray-700 font-semibold text-sm">
            <Calendar className="h-3 w-3" />
            Recent
          </h3>
          <button
            type="button"
            onClick={handleViewAllHistory}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
          >
            <List className="h-3 w-3" />
            History
          </button>
        </div>
        <div className="space-y-1">
          {recent.length > 0 ? (
            recent.map((call) => (
              <CallRow
                key={call.id}
                call={call}
                timeText={formatCallTime(call.startTime)}
                showJoinButton={canRejoinCall(call)}
                onJoin={() => handleJoinExternal(call)}
                onJoinFrontier={() => onJoinCall(call.id)}
                onViewReport={() => onViewReport(call.id)}
                isCompact={true}
              />
            ))
          ) : (
            <div className="text-center py-2 text-xs text-gray-400">
              No recent calls
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface CallRowProps {
  call: Call;
  timeText: string;
  showJoinButton: boolean;
  onJoin: () => void;
  onJoinFrontier: () => void;
  onViewReport: () => void;
  isCompact?: boolean;
}

function CallRow({
  call,
  timeText,
  showJoinButton,
  onJoin,
  onJoinFrontier,
  onViewReport,
  isCompact = false,
}: Readonly<CallRowProps>) {
  const isCompleted = ["completed", "processed", "ended", "finished"].includes(
    call.status,
  );

  if (isCompact) {
    return (
      <div
        className={`flex items-center justify-between py-0.5 px-2 rounded transition-colors ${
          showJoinButton ? "hover:bg-gray-50 cursor-pointer" : ""
        }`}
        {...(showJoinButton && {
          onClick: onJoinFrontier,
          onKeyDown: (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onJoinFrontier();
            }
          },
          tabIndex: 0,
          role: "button",
          "aria-label": `${isCompleted ? "Rejoin" : "Join"} ${call.summary ?? "call"}`,
        })}
      >
        <div className="flex items-center gap-1 flex-1 min-w-0">
          {call.meetingUrl && <CallServiceIcon url={call.meetingUrl} />}

          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-gray-900 truncate text-sm leading-tight">
              {call.summary ?? "Ad-hoc call"}
            </h4>
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {timeText}
              </div>
              <div className="flex items-center">
                <span className="text-gray-400">•</span>
                <span className="ml-1 font-mono text-gray-600">
                  {getDisplayIdFromCall(call)}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {isCompleted && (
            <Button
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation(); // Prevent row click
                onViewReport();
              }}
              className="flex items-center gap-1 h-6 px-1 text-xs"
            >
              <FileText className="h-3 w-3" />
              Report
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Original layout for non-compact mode
  return (
    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-100 hover:border-gray-200 transition-colors">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {call.meetingUrl && <CallServiceIcon url={call.meetingUrl} />}

        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-gray-900 truncate">
            {call.summary ?? "Untitled Call"}
          </h4>
          <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {timeText}
            </span>
            <Badge variant="outline" className="text-xs">
              {getDesktopStatusText(call)}
            </Badge>
            {call.recallBotId && (
              <span className="flex items-center gap-1">
                <Video className="h-3 w-3" />
                <span className="font-mono">
                  {call.recallBotId.slice(0, 8)}...
                </span>
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {showJoinButton && (
          <>
            <Button
              size="sm"
              onClick={onJoin}
              className="flex items-center gap-1"
            >
              <Phone className="h-3 w-3" />
              Join
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onJoinFrontier}
              className="flex items-center gap-1"
            >
              <Video className="h-3 w-3" />🤖
            </Button>
          </>
        )}

        {isCompleted && (
          <Button
            size="sm"
            variant="outline"
            onClick={onViewReport}
            className="flex items-center gap-1"
          >
            <FileText className="h-3 w-3" />
            Report
          </Button>
        )}
      </div>
    </div>
  );
}
